import { AnalyticsSettingsEvent } from 'analytics-api';
import { IdtComponentDefinition } from 'shared/models/idt-components/idt-component.models';

import { CommonSettingsStyles, CommonStyles, TypeFlexAlignSelf, TypeFlexBasis } from '../../cms/cms-shared/common-settings-styles/common-settings-styles.models';


export const PRODUCT_NAME_DEFAULT_VALUE = '<p><span data-entity="placeholder">{{productName}}</span></p>';
export const PRODUCT_DESCRIPTION_DEFAULT_VALUE = '<p><span data-entity="placeholder">{{productDescription}}</span></p>';
export const PRODUCT_SIZE_ADDITIONAL_INFO_DEFAULT_VALUE = '<p><span data-entity="placeholder">{{additionalInfo}}</span></p>';

export type DeclarativeFieldType =
  'plainText' |
  'richText' |
  'richTextByDevice' |
  'datetime' |
  'units' |
  'image' |
  'icon' |
  'boolean' |
  'video' |
  'options' |
  'optionButtons' |
  'numberForDesktopAndMobile' |
  'color' |
  'linkedUnits' |
  'linkedImages' |
  'linkedProducts' |
  'linkedCategories' |
  'segmentedByConfig' |
  'linkedCollection' |
  'category' |
  'product' |
  'link' |
  'group' |
  'pieceTable' |
  'slidesSelector'|
  'productSettingsSelector'|
  'relatedProductGroup'|
  'tableColumns' |
  'groupCollapsable' |
  'groupToggleable' |
  'groupTabs' |
  'groupColumns' |
  'marquee' |
  'responsiveSize' |
  'menuAnimatedItems' |
  'prices' |
  'productPurchaseOptions';

export type TypeOfOverflow = 'auto' | 'hidden' | 'scroll' | 'visible';

export type OptionValue = string | { value: string; label: string; shouldDisplayWhenSelected?: string };

export type AvailablePlatform = 'web' | 'apps';

export interface DeclarativeField {
  name: string;
  type: DeclarativeFieldType;
  /** If true, a groupToggleable field will automatically be set to false if all its children do */
  autoCollapse?: boolean;
  label?: string;
  mandatory?: boolean;
  defaultValue?: any;
  disableTranslations?: boolean;
  values?: OptionValue[]; // eslint-disable-line @typescript-eslint/ban-types
  fields?: DeclarativeField[];
  tabs?: DeclarativeTab[];
  key?: string;
  jsonPath?: string;
  section?: 'general' | 'styles';
  flattenFields?: boolean;
  dropdownMode?: boolean;
  placeholder?: string;
  validators?: string[];
  isExcluded?: boolean;
  isFolded?: boolean;
  helpText?: string;
  canBeDeleted?: boolean;
  disableUnits?: string[];
  // if true, the component will include a padding selector. Only implemented for richTextByDevice
  includePaddingSelector?: boolean;
}

export interface DeclarativeTab {
  label: string;
  fields: string[];
}

export interface IdtComponentSettings {
  general: unknown;
  styles: CommonStyles | CommonStyles[];
  analytics?: { events: AnalyticsSettingsEvent[] };
}

export interface ResponsiveSlotSettings extends Partial<Omit<SlotSettings, 'id' | 'responsiveConfig'>> {
  id: string;
}

export interface SlotSettings {
  id?: string;
  alignSelf?: TypeFlexAlignSelf | null;
  flexGrow: number;
  flexShrink: number;
  flexBasis: TypeFlexBasis;
  minWidth?: string | null;
  width: string;
  maxWidth?: string | null;
  minHeight?: string | null;
  height: string;
  maxHeight?: string | null;
  overflow?: TypeOfOverflow;
  hidden?: boolean;
  responsiveConfig: ResponsiveSlotSettings[];
}

// The enum has to be a string one because we need to hardcode the names in definition.json files
export enum CmsComponentGroups {
  Basic = 'Basic',
  Media = 'Media',
  Carousels = 'Carousels',
  Components = 'Components',
  Forms = 'Forms',
  Fragments = 'Fragments'
}

export interface IdtComponentCmsDefinition extends Partial<IdtComponentDefinition> {
  name: string;
  group: CmsComponentGroups;
  availablePlatforms: AvailablePlatform[];
  isDeprecated?: boolean;
  isNew?: boolean;
  isOnlyAvailableWithCollections?: boolean;
  initialSettings?: (componentInstanceName: string) => IdtComponentSettings;
  fields?: DeclarativeField[];
  analytics?: string[];
  disableStyles?: CommonSettingsStyles[];
}

export interface IdtImage {
  image: string | null;
  width?: number | string; // TODO: remove string from width and height
  height?: number | string;
  showVectorImage?: boolean;
  android?: Omit<IdtImage, 'showVectorImage' | 'android' | 'iOS'>;
  iOS?: Omit<IdtImage, 'showVectorImage' | 'android' | 'iOS'>;
}

export interface IdtLink {
  type?: string;
  category?: string;
  product?: string;
  src?: string;
  pieceName?: string;
  categoryId?: string;
}
